import {
  type CalendarDate,
  DateFormatter,
  getLocalTimeZone,
  now,
  parseAbsolute,
  parseDate,
  toCalendarDate,
  toTime,
  type ZonedDateTime
} from "@internationalized/date";
import type {
  MealReminderResponse,
  MealRemindersPatchRequest
} from "@9amhealth/openapi";
import { getSupportedUserLocale } from "src/lib/i18next";

export type MealReminderSlot = "breakfast" | "lunch" | "snack" | "dinner";

export interface MealReminderTime {
  time: string;
  enabled: boolean;
}

export type MealReminderSchedule = Record<MealReminderSlot, MealReminderTime>;

export const MEAL_REMINDER_SLOTS: MealReminderSlot[] = [
  "breakfast",
  "lunch",
  "snack",
  "dinner"
];

export const DEFAULT_MEAL_REMINDER_SCHEDULE: MealReminderSchedule = {
  breakfast: { time: "07:30", enabled: true },
  lunch: { time: "12:00", enabled: true },
  snack: { time: "15:00", enabled: false },
  dinner: { time: "18:00", enabled: true }
};

const isMealReminderSlot = (key: string): key is MealReminderSlot =>
  (MEAL_REMINDER_SLOTS as string[]).includes(key);

/** Keys outside the four slots (e.g. a weekend reminder Sophie added) are left out. */
export const scheduleFromReminders = (
  reminders: MealReminderResponse[]
): MealReminderSchedule => {
  const schedule = { ...DEFAULT_MEAL_REMINDER_SCHEDULE };
  for (const { key, time, enabled } of reminders) {
    if (isMealReminderSlot(key)) {
      schedule[key] = { time, enabled };
    }
  }
  return schedule;
};

export const remindersPatchFromSchedule = (
  schedule: MealReminderSchedule
): MealRemindersPatchRequest => ({
  enabled: hasActiveReminder(schedule),
  reminders: MEAL_REMINDER_SLOTS.map((key) => ({ key, ...schedule[key] }))
});

export const reminderTimeToZoned = (time: string): ZonedDateTime => {
  const [hour, minute] = time.split(":").map(Number);
  return now(getLocalTimeZone()).set({
    hour,
    minute,
    second: 0,
    millisecond: 0
  });
};

export const zonedToReminderTime = (value: ZonedDateTime): string =>
  toTime(value).toString().slice(0, 5);

export const formatReminderTime = (time: string): string =>
  new DateFormatter(getSupportedUserLocale(), {
    hour: "numeric",
    minute: "2-digit"
  }).format(reminderTimeToZoned(time).toDate());

export const scheduleTrackingData = (
  schedule: MealReminderSchedule
): Record<MealReminderSlot, string> =>
  Object.fromEntries(
    MEAL_REMINDER_SLOTS.map((slot) => [
      slot,
      schedule[slot].enabled ? schedule[slot].time : "off"
    ])
  ) as Record<MealReminderSlot, string>;

export const CALIBRATION_DAYS = 7;

const STRIP_DAYS_BEFORE_TODAY = 3;

/** Day-7 copy switches on this many distinct logged days (PROD-1407). */
export const CALIBRATION_STRONG_START_DAYS = 3;

export type MealRemindersPhase =
  | "notStarted"
  | "calibration"
  | "calibrationEnded"
  | "afterCalibration";

export type CalibrationEndChoice = "keepOn" | "adjust" | "setReminders";

export const hasActiveReminder = (schedule: MealReminderSchedule): boolean =>
  MEAL_REMINDER_SLOTS.some((slot) => schedule[slot].enabled);

/** Paused reminders read as all-off, whatever times are still saved. */
export const scheduleAsShown = (
  schedule: MealReminderSchedule,
  remindersOn: boolean
): MealReminderSchedule =>
  remindersOn
    ? schedule
    : (Object.fromEntries(
        MEAL_REMINDER_SLOTS.map((slot) => [
          slot,
          { ...schedule[slot], enabled: false }
        ])
      ) as MealReminderSchedule);

const shiftDays = (day: string, days: number): string =>
  parseDate(day).add({ days }).toString();

/** The backend's zone for members without a `user.timezone` preference. */
export const DEFAULT_MEMBER_TIME_ZONE = "America/Los_Angeles";

export const uploadDayKey = (createdAt: string, timeZone: string): string =>
  toCalendarDate(parseAbsolute(createdAt, timeZone)).toString();

export const calibrationEndDay = (startedOn: string): string =>
  shiftDays(startedOn, CALIBRATION_DAYS - 1);

/**
 * Calibration is the member's first 7 days of logging, from the day of their
 * first meal ever, whether or not reminders are on. The day-7 card shows on
 * day 7 only; from day 8 the after-calibration card follows on its own.
 */
export interface MealRemindersPhaseInput {
  today: string;
  startedOn?: string;
  choice?: CalibrationEndChoice;
  /** The backend's verdict wins if it ever disagrees with the local first-meal day. */
  calibrationCompleted: boolean;
}

export const mealRemindersPhase = ({
  today,
  startedOn,
  choice,
  calibrationCompleted
}: MealRemindersPhaseInput): MealRemindersPhase => {
  if (calibrationCompleted) {
    return "afterCalibration";
  }
  if (!startedOn) {
    return "notStarted";
  }
  const endsOn = calibrationEndDay(startedOn);
  if (today < endsOn) {
    return "calibration";
  }
  return today === endsOn && !choice ? "calibrationEnded" : "afterCalibration";
};

export interface LogOverviewDay {
  date: string;
  label: string;
  logged: boolean;
  isToday: boolean;
}

const weekdayLetter = (date: CalendarDate): string =>
  new DateFormatter(getSupportedUserLocale(), { weekday: "narrow" }).format(
    date.toDate(getLocalTimeZone())
  );

/**
 * Today sits in the middle of the strip, with three days on either side.
 * Days before calibration started never show as logged, even with a backdated meal.
 */
export const buildLogOverview = (
  today: string,
  loggedDays: ReadonlySet<string>,
  startedOn: string | undefined
): LogOverviewDay[] =>
  Array.from({ length: CALIBRATION_DAYS }, (_, index) => {
    const date = parseDate(today).add({
      days: index - STRIP_DAYS_BEFORE_TODAY
    });
    const key = date.toString();
    return {
      date: key,
      label: weekdayLetter(date),
      logged:
        startedOn !== undefined && key >= startedOn && loggedDays.has(key),
      isToday: key === today
    };
  });

export const countCalibrationDaysLogged = (
  startedOn: string | undefined,
  today: string,
  loggedDays: ReadonlySet<string>
): number => {
  if (!startedOn) {
    return 0;
  }
  const endsOn = calibrationEndDay(startedOn);
  return [...loggedDays].filter(
    (day) => day >= startedOn && day <= today && day <= endsOn
  ).length;
};
